/**
 * Группа взаимоисключающих вариантов.
 *
 * Реализована как radiogroup, а не набор кнопок: это даёт корректную
 * навигацию с клавиатуры и правильное объявление экранным читалкой (UX-12).
 */

export interface ChoiceGroupOption<T extends string> {
  readonly value: T;
  readonly label: string;
}

export interface ChoiceGroupProps<T extends string> {
  readonly legend: string;
  readonly name: string;
  readonly options: readonly ChoiceGroupOption<T>[];
  readonly value: T;
  readonly onChange: (value: T) => void;
  /**
   * Ошибка всей группы, а не отдельного варианта.
   *
   * Выбор неверен как выбор: сказать это у одного из вариантов значило бы
   * обвинить именно его (ТЗ UI-27).
   */
  readonly error?: string | undefined;
}

export function ChoiceGroup<T extends string>({
  legend,
  name,
  options,
  value,
  onChange,
  error,
}: ChoiceGroupProps<T>): React.JSX.Element {
  const errorId = `${name}-error`;

  return (
    <fieldset
      className={`t2x-choice${error === undefined ? '' : ' t2x-choice--error'}`}
      aria-describedby={error === undefined ? undefined : errorId}
      aria-invalid={error === undefined ? undefined : true}
    >
      <legend className="t2x-choice__legend">{legend}</legend>
      <div className="t2x-choice__options">
        {options.map((option) => (
          <label key={option.value} className="t2x-choice__option">
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
      {error !== undefined && (
        <p className="t2x-choice__error" id={errorId}>
          {error}
        </p>
      )}
    </fieldset>
  );
}
